import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Box,
  IconButton,
  Chip,
  Tooltip,
  Alert,
  FormControlLabel,
  Switch,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import UndoIcon from '@mui/icons-material/Undo';
import axios from '../../api/client';
import { toast } from 'react-toastify';
import ScrollTable from '../ScrollTable';
import TableToolbar from '../TableToolbar';
import useTableSearch from '../../hooks/useTableSearch';

// Mirrors backend/utils/noticeTemplates.js.
const TEMPLATES = { standard: 'Standard', formal: 'Formal (legal)' };

const EMPTY = { name: '', description: '', documentTemplate: 'standard', requiresEffectiveDate: false };

const NoticeCategoryManagement = () => {
  const [categories, setCategories] = useState([]);

  // Client-side: this screen loads every row, so filtering here searches all of
  // them. Screens that page on the server search there instead.
  const {
    query, setQuery, reset,
    rows: visibleCategories, resultCount, totalCount
  } = useTableSearch(categories, {
    searchFields: ['name', 'description'],
    filters: {}
  });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(EMPTY);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await axios.get('/notice-categories');
      setCategories(res.data || []);
    } catch (error) {
      toast.error('Error fetching notice categories');
    }
  };

  const handleOpen = (category = null) => {
    if (category) {
      setEditing(category._id);
      setFormData({
        name: category.name,
        description: category.description || '',
        documentTemplate: category.documentTemplate || 'standard',
        requiresEffectiveDate: !!category.requiresEffectiveDate,
      });
    } else {
      setEditing(null);
      setFormData(EMPTY);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
    setFormData(EMPTY);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Category name is required');
      return;
    }

    setLoading(true);
    try {
      if (editing) {
        const res = await axios.put(`/notice-categories/${editing}`, formData);
        const issued = res.data?.existingNotices || 0;
        toast.success(
          issued > 0
            ? `Category updated. ${issued} notice(s) already issued keep the document they were served with.`
            : 'Notice category updated'
        );
      } else {
        await axios.post('/notice-categories', formData);
        toast.success('Notice category created');
      }
      fetchCategories();
      handleClose();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error saving notice category');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (category) => {
    if (!window.confirm(`Delete the "${category.name}" category? If any notices already use it, it will be deactivated instead so those records stay intact.`)) {
      return;
    }
    try {
      const res = await axios.delete(`/notice-categories/${category._id}`);
      toast.success(res.data?.message || 'Notice category deleted');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error deleting notice category');
    }
  };

  const handleReactivate = async (category) => {
    try {
      await axios.put(`/notice-categories/${category._id}`, { isActive: true });
      toast.success('Notice category reactivated');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error reactivating notice category');
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight={700}>Notice Categories</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          Add Category
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        The kinds of notice you issue — Evacuation, Termination, Rent Increment and so on.
        Staff choose one when raising a notice. A category also decides how the notice
        prints: <strong>Formal</strong> produces the legal document (addressed to the tenant,
        notice period calculated, signature block), <strong>Standard</strong> a plain notice.
      </Alert>

      <TableToolbar
        search={query}
        onSearchChange={setQuery}
        searchPlaceholder="Category name"
        onReset={reset}
        resultCount={resultCount}
        totalCount={totalCount}
      />
      <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell>Category</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Document</TableCell>
              <TableCell>Effective Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleCategories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No notice categories yet. Click "Add Category" to create one.
                </TableCell>
              </TableRow>
            ) : (
              visibleCategories.map((category) => (
                <TableRow key={category._id}>
                  <TableCell sx={{ fontWeight: 500 }}>{category.name}</TableCell>
                  <TableCell>{category.description || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={category.documentTemplate === 'formal' ? 'warning' : 'default'}
                      label={TEMPLATES[category.documentTemplate] || category.documentTemplate}
                    />
                  </TableCell>
                  <TableCell>{category.requiresEffectiveDate ? 'Required' : 'Not asked for'}</TableCell>
                  <TableCell>
                    <Chip
                      label={category.isActive ? 'Active' : 'Inactive'}
                      color={category.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleOpen(category)} color="primary">
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    {category.isActive ? (
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDelete(category)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Reactivate">
                        <IconButton size="small" onClick={() => handleReactivate(category)} color="success">
                          <UndoIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </ScrollTable>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Notice Category' : 'Add Notice Category'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Category Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              margin="normal"
              required
              helperText="e.g. Evacuation Notice, Rent Increment Notice"
            />
            <TextField
              fullWidth
              select
              label="Document Layout"
              value={formData.documentTemplate}
              onChange={(e) => setFormData({ ...formData, documentTemplate: e.target.value })}
              margin="normal"
              required
              helperText={
                formData.documentTemplate === 'formal'
                  ? 'Legal format: addressed to the tenant, notice period calculated, signature block'
                  : 'Plain notice: header, details and reason'
              }
            >
              {Object.entries(TEMPLATES).map(([key, label]) => (
                <MenuItem key={key} value={key}>{label}</MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              margin="normal"
              multiline
              rows={2}
              helperText="Optional — helps staff pick the right category"
            />
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Switch
                  checked={formData.requiresEffectiveDate}
                  onChange={(e) => setFormData({ ...formData, requiresEffectiveDate: e.target.checked })}
                />
              }
              label="Require an effective date"
            />
            <Typography variant="caption" color="text.secondary" display="block">
              The date the notice takes effect. The formal layout measures the notice period
              from it, so leave this on for anything demanding vacant possession.
            </Typography>
            {editing && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Notices already issued keep the document they were served with. Changes here
                apply to notices raised from now on.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading || !formData.name.trim()}>
            {loading ? 'Saving...' : editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default NoticeCategoryManagement;
